import React from 'react';
import { Pencil, Trash2, FileText } from 'lucide-react';
import { eventTypeLabel, originLabel } from '../../utils/litigationLabels';

function fmt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const originBadge = {
  self_initiated: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  opposing_initiated: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  court_initiated: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  third_party_initiated: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  scheduled: 'bg-green-500/20 text-green-300 border-green-500/30',
};

const LitigationEventTimeline = ({ events, onEdit, onDelete }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-10 bg-virgo-navy-800/30 rounded-xl border border-virgo-teal-700/30">
        <p className="text-virgo-teal-300">No litigation events logged yet.</p>
        <p className="text-virgo-teal-500 text-sm mt-1">Use “Log Event” to record the first pleading, motion, or discovery.</p>
      </div>
    );
  }
  const sorted = [...events].sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));

  return (
    <div className="space-y-3">
      {sorted.map((ev) => (
        <div key={ev.id} id={`litevent-${ev.id}`}
          className="p-4 rounded-xl border border-virgo-teal-700/30 bg-virgo-navy-800/40 flex items-start gap-4 scroll-mt-24">
          <div className="w-24 shrink-0 text-right">
            <p className="text-sm font-semibold text-white">{fmt(ev.occurred_at)}</p>
            {ev.service_date && <p className="text-xs text-virgo-teal-500 mt-0.5">svc {fmt(ev.service_date)}</p>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white">{eventTypeLabel(ev.event_type)}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs border ${originBadge[ev.event_origin] || originBadge.third_party_initiated}`}>
                {originLabel(ev.event_origin)}
              </span>
            </div>
            {ev.description && <p className="text-sm text-virgo-teal-200 mt-1">{ev.description}</p>}
            {ev.rule_citation && <p className="text-xs text-virgo-teal-400 mt-1">{ev.rule_citation}</p>}
            {ev.hearing_date && <p className="text-xs text-virgo-teal-400 mt-1">Hearing: {fmt(ev.hearing_date)}</p>}
            {ev.related_document_id && (
              <a href={`#doc-${ev.related_document_id}`} className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-1">
                <FileText className="w-3 h-3" /> Linked document
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onEdit(ev)} title="Edit"
              className="p-1.5 text-virgo-teal-400 hover:text-cyan-300 hover:bg-virgo-navy-600 rounded-lg transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(ev.id)} title="Delete"
              className="p-1.5 text-virgo-teal-400 hover:text-red-400 hover:bg-virgo-navy-600 rounded-lg transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LitigationEventTimeline;
